<template>
  <transition
    name="fade"
    appear
  >
    <div
      v-if="isOpen"
      class="cookie fixed w-100"
    >
      <div
        class="cookie-container flex c-bg-white between-xs py30 px40"
      >
        <div class="flex middle-xs">
          <span>
            {{ message }}
            <a
              class="c-primary pointer"
              :href="privacyLink"
            >
              {{ detailsLinkText }}
            </a>
          </span>
        </div>
        <div class="cookie-actions">
          <button-primary
            data-testid="acceptAllCookiesButton"
            :disabled="requestingSave"
            class="cookie-button p10 bold"
            @click="acceptAll"
          >
            {{ $t('Accept all') }}
          </button-primary>
          <button-primary
            data-testid="rejectAllCookiesButton"
            :disabled="requestingSave"
            class="cookie-button cookie-button--secondary p10 bold"
            @click="rejectAll"
          >
            {{ $t('Reject all') }}
          </button-primary>
          <button-primary
            data-testid="cookieSettingsButton"
            class="cookie-button cookie-button--secondary p10 bold"
            @click="openGdprModal"
          >
            {{ $t('Cookie settings') }}
          </button-primary>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import i18n from '@vue-storefront/i18n'
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus'
import { CONSENT_UPDATED_EVENT, COOKIE_POLICY_PATH, hasConsentChoice } from 'src/modules/gdpr-cookie-setup/helpers'
import { COOKIE_DATA_GETTER, MODAL_NAME } from 'src/modules/gdpr-cookie-setup'
import { CookieUserStorage } from 'src/modules/gdpr-cookie-setup/mixin/cookieUserStorage'
import ButtonPrimary from 'theme/components/theme/ButtonPrimary'
import { mapGetters, mapState } from 'vuex'

export default {
  components: {
    ButtonPrimary
  },
  mixins: [CookieUserStorage],
  props: {
    detailsLinkText: {
      type: String,
      default: i18n.t('cookie policy')
    },
    message: {
      type: String,
      default: i18n.t('We use cookies to make the site work, to analyse how it is used and to personalise ads. You choose which cookies we may use. Read more in our')
    }
  },
  data () {
    return {
      isOpen: false,
      requestingSave: false,
      privacyLink: COOKIE_POLICY_PATH
    }
  },
  computed: {
    ...mapState({
      isMobile: state => state.ui.isMobile
    }),
    ...mapGetters({
      getCookieSetupData: COOKIE_DATA_GETTER
    })
  },
  async created () {
    this.isOpen = !await hasConsentChoice()
  },
  beforeMount () {
    EventBus.$on(CONSENT_UPDATED_EVENT, this.dismiss)
  },
  beforeDestroy () {
    EventBus.$off(CONSENT_UPDATED_EVENT, this.dismiss)
  },
  methods: {
    /**
     * @return {Promise<void>}
     */
    async acceptAll () {
      await this.persistChoice(this.getAllCookieGroupIds())
    },
    /**
     * @return {Promise<void>}
     */
    async rejectAll () {
      await this.persistChoice(this.getEssentialsCookieGroupIds())
    },
    /**
     * @param {Array} selectedGroupIds
     * @return {Promise<void>}
     */
    async persistChoice (selectedGroupIds) {
      if (this.requestingSave) {
        return
      }
      this.requestingSave = true

      await this.saveConsentChoice(selectedGroupIds)

      this.requestingSave = false
      this.dismiss()
    },
    /**
     * @return {void}
     */
    dismiss () {
      this.isOpen = false
    },
    /**
     * @return {void}
     */
    openGdprModal () {
      this.$bus.$emit('modal-show', MODAL_NAME)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~theme/css/base/global_vars";
@import "~theme/css/variables/colors";
@import "~theme/css/variables/grid";
@import "~theme/css/helpers/functions/color";
@import "~theme/css/helpers/functions/z-index-selectors";

$color-icon: color(black);
$bg-icon: color(suva-gray);

.cookie {
  z-index: zIndex(cookie-banner);
  bottom: 0;

  &-container {
    max-width: 70%;
    margin: 0 auto;
    flex-wrap: wrap;
    row-gap: 20px;
    box-shadow: 0 0 15px 0 rgba(0, 0, 0, 0.3);
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    font-size: 24px;
    line-height: 29px;
    align-items: center;

    @include mediaQuery(null, md) {
      font-size: 16px;
      line-height: 19px;
      max-width: 770px;
      flex-direction: column;
      padding: 20px 15px 30px 15px;
    }

    & > .flex {
      flex: 1 1 420px;
      padding-right: 15px;

      @include mediaQuery(null, md) {
        flex-basis: auto;
        padding-bottom: 20px;
        padding-right: 0;
      }
    }
  }

  &-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
    align-items: center;

    @include mediaQuery(null, md) {
      flex-direction: column;
      width: 100%;
    }
  }

  /deep/ &-button {
    max-height: 58px;
    padding: 15px 30px;
    white-space: nowrap;

    &--secondary {
      background-color: color(white);
      color: color(primary);
      border: 2px solid color(primary);

      &:hover,
      &:focus {
        background-color: color(primary);
        color: color(white);
      }
    }
  }

  @include mediaQuery(null, md) {
    /deep/ &-button {
      font-size: 20px;
      align-self: center;
      width: 100%;
    }
  }
}

.icon:hover {
  color: $color-icon;
  background-color: $bg-icon;
}
</style>
